/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import {departmentSubunitService, taskService} from "services"
import {KlassappTableHOC} from "HOC"
import {handleError, toastSuccess, toastWarning} from "helpers"
import {TaskType, TasksTab} from "types/tasks"
import {useMutation, useVisible} from "hooks"
import {useHistory} from "react-router-dom"
import {useQuery} from "@tanstack/react-query"

function StudentInitializableTab(props) {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        orderField,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        isLoading,
        isShowTableHeaderAction,
        isLoadedTableFuncs,
        search
    } = props
    const {t} = useTranslation(["tasks", "common"])
    const startConfirmPopup = useVisible(false)
    const history = useHistory()
    const [selectedTaskId, setSelectedTaskId] = useState(null)
    const taskTypeLabel = {
        [TaskType.DIGITAL_DOCUMENT]: "Digital Document",
        [TaskType.SCAN_UPLOAD]: "Scan Upload"
    }

    const {
        data: departmentSubunits,
        isLoading: isLoadingDepartmentSubunits,
        isSuccess
    } = useQuery(["departmentSubunits"], async () => {
        const response = await departmentSubunitService.getSubunits({
            range: {pageSize: 50, page: 1}
        })
        return response.data
    })

    const startTaskMutation = useMutation((selectedTaskId: number) => taskService.initializeTasks(selectedTaskId), {
        onSuccess: (response) => {
            const {redirectToAnswer, taskUser} = response
            if (redirectToAnswer) {
                let searchUrl = `id=${taskUser.taskId}`
                if (taskUser.subTaskId) {
                    searchUrl += `&subTaskId=${taskUser.subTaskId}`
                }
                toastSuccess("Task started successfully")
                history.push({
                    pathname: `/tasks/submissions`,
                    search: searchUrl,
                    state: {backUrl: `/tasks?tab=${TasksTab.STUDENT_INITIALIZABLE}`}
                })
            } else {
                startConfirmPopup.close()
                toastSuccess("Task started successfully")
                getData()
            }
            setSelectedTaskId(null)
        },
        onError: (error) => handleError(error)
    })

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs && isSuccess) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order, search, isSuccess])

    useEffect(() => {
        props.updateTableHeaderActions()
    }, [data])

    function getPageTitle() {
        return ""
    }

    const getParams = () => {
        const {page, pageSize, orderField} = props
        const params = {
            filter: {search},
            range: {page, pageSize},
            sort: {
                orderBy: orderField.field ?? "createdAt",
                orderDir: orderField.order ?? "DESC"
            }
        }
        return params
    }

    const getData = async () => {
        try {
            dispatch({isLoading: true})
            const params = getParams()
            const {data, total} = await taskService.getStudentInitializableTasks(params)
            const tasks = data.map((item) => ({
                ...item,
                departmentSubunitName: departmentSubunits?.find((subunit) => subunit.subunitId === item.departmentId)
                    ?.name
            }))
            dispatch({data: tasks, total, isLoading: false})
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const getFields = () => {
        return [t("tasks.code"), t("tasks.name"), t("tasks.type"), t("tasks.department"), t("tasks.added")]
    }

    const getColumns = () => {
        return [
            {
                title: t("tasks.code"),
                field: "code",
                style: {minWidth: "120px"},
                sortable: true
            },
            {
                title: t("tasks.name"),
                field: "name",
                style: {minWidth: "150px"},
                sortable: true
            },
            {
                title: t("tasks.type"),
                field: "type",
                style: {minWidth: "150px"},
                orderField: "type",
                render: (type: TaskType) => taskTypeLabel[type],
                sortable: true
            },
            {
                title: t("tasks.department"),
                field: "departmentSubunitName",
                style: {minWidth: "120px"}
            },
            {
                title: t("tasks.added"),
                field: "createdAt",
                fieldType: "date",
                style: {width: "140px"},
                sortable: true
            }
        ]
    }

    const onUpdateRowData = (updatedData) => {
        const data = props.getCurrentData()
        const newData = data.map((item) => {
            item.isChecked = updatedData.isChecked && item.id === updatedData.id
            return item
        })
        props.dispatch({data: newData})
    }

    const handleConfirmStartTask = async () => {
        if (!selectedTaskId) {
            toastWarning("You need to select one task")
            return
        }
        startTaskMutation.mutate(selectedTaskId)
    }

    const onClickRowItem = (rowData) => {
        setSelectedTaskId(rowData.id)
        startConfirmPopup.open()
    }

    return (
        <>
            <KlassappTableHeader
                isShowAction={false}
                actions={[]}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                isLoading={isLoading || isLoadingDepartmentSubunits}
                orderField={orderField}
                allFields={allFields}
                isShowCheckedColumn={false}
                isShowCheckedColumnHeader={false}
                onUpdateRowData={onUpdateRowData}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
            <ConfirmPopup
                isVisible={startConfirmPopup.isVisible}
                title={t("tasks.startTaskConfirmTitle")}
                description={t("tasks.startTaskConfirmDescription")}
                confirmLabel={t("tasks.startTask")}
                onClose={startConfirmPopup.close}
                loading={startTaskMutation.isLoading}
                onConfirm={handleConfirmStartTask}
            />
        </>
    )
}

export default KlassappTableHOC(StudentInitializableTab)
